// FBK26-F6.4 (ADR-036 della piattaforma, settima dotazione STD-6): feedback sul portale con @brandart/feedback-kit. Un
// solo pulsante rotondo in basso a destra («Lascia un feedback» ⌘⇧S, «I miei feedback»), lingua del portale. Il kit
// parla solo col backend (`/api/v1/feedback`), che mette l'utente dalla sessione e firma verso agent-runtime.
// Nel portale «segnalazione» è il red flag: per questo i testi dicono sempre «feedback».
// Oscuramento: valori a rischio, importi dei claim, costo della non qualità, testo del report e conversazione con
// l'Assistente portano `data-sensitive` nelle viste (KPI con `sensibile`); qui si aggiungono i campi numerici (nel
// portale ogni InputNumber è un importo o il cambio) e le aree di testo libero.
import { useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { EN_TEXTS, FeedbackButton, FeedbackProvider, type FeedbackConfig } from "@brandart/feedback-kit";
import "@brandart/feedback-kit/styles.css";
import { API_PREFIX } from "@qa/shared";
import { traccia } from "../aiuto/usage";

/** Host di produzione del portale: altrove (qa-test, localhost) i feedback sono `test`. */
export const QA_PROD_HOST = "qa.brandartlab.com";

/**
 * Oscurati anche senza `data-sensitive` nel markup:
 * - `.ant-input-number`: ogni campo numerico del portale è un importo (valore a rischio, costi, claim) o il cambio;
 * - `textarea`: problema, causa, impatto, note e 8D possono contenere clienti e importi.
 */
export const FEEDBACK_MASK_SELECTORS = [".ant-input-number", "textarea"];

export const feedbackEnvironment = (hostname: string): FeedbackConfig["environment"] => (hostname === QA_PROD_HOST ? "prod" : "test");

type Evento = Parameters<NonNullable<FeedbackConfig["onEvent"]>>[0];

/** KPI dei feedback: solo tipo, modo e conteggi (mai il commento né l'immagine), come da contratto analytics. */
export function inviaEventoFeedback(e: Evento) {
  switch (e.type) {
    case "feedback_mode_opened":
      return traccia("feedback_mode_opened", { entry: e.entry });
    case "feedback_submitted":
      return traccia("feedback_submitted", { kind: e.kind, anchor_mode: e.anchor_mode, has_image: e.has_image, masked_count: e.masked_count });
    case "feedback_cancelled":
      return traccia("feedback_cancelled", { step: e.step });
    case "feedback_status_viewed":
      return traccia("feedback_status_viewed", { unseen: e.unseen });
  }
}

interface Props {
  user: { name: string; email: string };
  version: string;
  commit: string | null;
  children: ReactNode;
}

export function FeedbackLayer({ user, version, commit, children }: Props) {
  const { i18n } = useTranslation();
  // Dove i feedback non sono configurati (locale, CI) il pulsante non compare, invece di un invio che fallisce.
  const status = useQuery({
    queryKey: ["feedback-status"],
    queryFn: async () => {
      const res = await fetch(`${API_PREFIX}/feedback/status`, { credentials: "same-origin", headers: { Accept: "application/json" } });
      return res.ok ? ((await res.json()) as { enabled?: boolean }).enabled === true : false;
    },
    staleTime: Infinity,
    retry: false,
  });
  const lang = i18n.language;
  const config = useMemo<FeedbackConfig>(
    () => ({
      app: "qa",
      appVersion: version,
      buildSha: commit ?? undefined,
      environment: feedbackEnvironment(typeof location === "undefined" ? "" : location.hostname),
      endpoint: `${API_PREFIX}/feedback`,
      user,
      enabled: status.data === true,
      theme: "light",
      maskSelectors: FEEDBACK_MASK_SELECTORS,
      texts: lang === "en" ? EN_TEXTS : undefined,
      onEvent: inviaEventoFeedback,
    }),
    [version, commit, user, status.data, lang],
  );
  return (
    <FeedbackProvider config={config}>
      {children}
      <FeedbackButton />
    </FeedbackProvider>
  );
}
